export const ENABLE_DEVTOOLS_BLOCKER =
  process.env.REACT_APP_DISABLE_DEVTOOLS === "true";

const COUNTDOWN_SECONDS = 5;
const BROADCAST_CHANNEL_NAME = "devtools_blocker_channel";

let isBlocked = false;
let broadcastChannel: BroadcastChannel | null = null;

// Detect if running on Mac
const isMac = (): boolean => {
  return (
    navigator.platform.toUpperCase().indexOf("MAC") >= 0 ||
    navigator.userAgent.toUpperCase().indexOf("MAC") >= 0
  );
};

// Handle devtools detection - clear page and show warning
const handleDevToolsDetected = (notifyOtherTabs: boolean = true): void => {
  if (isBlocked) return;
  isBlocked = true;

  // Notify other tabs to logout as well
  if (notifyOtherTabs && broadcastChannel) {
    broadcastChannel.postMessage({ type: "DEVTOOLS_DETECTED" });
  }

  // Clear console immediately
  console.clear();

  // Clear ALL storage - localStorage, sessionStorage, cookies
  localStorage.clear();
  sessionStorage.clear();

  // Clear all cookies
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });

  // Clear the entire document and show warning page (no network requests)
  document.open();
  document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Security Warning</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          background: rgba(0, 0, 0, 0.95);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .content {
          text-align: center;
          color: #fff;
          padding: 40px;
          max-width: 500px;
        }
        .icon { font-size: 64px; margin-bottom: 20px; }
        h2 { font-size: 24px; margin-bottom: 16px; color: #ff6b6b; }
        p { font-size: 16px; line-height: 1.6; margin-bottom: 16px; color: #ccc; }
        .countdown { font-size: 48px; font-weight: bold; color: #ff6b6b; margin: 20px 0; }
        .warning-text { color: #ffcc00; font-size: 14px; }
      </style>
    </head>
    <body>
      <div class="content">
        <div class="icon">⚠️</div>
        <h2>Security Warning</h2>
        <p>Developer tools usage has been detected.</p>
        <p>You will be logged out in:</p>
        <div class="countdown" id="countdown-timer">${COUNTDOWN_SECONDS}</div>
        <p class="warning-text">seconds</p>
      </div>
      <script>
        // Keep clearing everything continuously
        setInterval(() => {
          console.clear();
          localStorage.clear();
          sessionStorage.clear();
        }, 100);

        let secondsLeft = ${COUNTDOWN_SECONDS};
        const countdownElement = document.getElementById('countdown-timer');
        const interval = setInterval(() => {
          secondsLeft--;
          countdownElement.textContent = secondsLeft;
          if (secondsLeft <= 0) {
            clearInterval(interval);
            document.body.innerHTML = \`
              <div class="content">
                <div class="icon">⚠️</div>
                <h2>You have been logged out</h2>
                <p>To continue using the application:</p>
                <p style="color: #ffcc00; font-weight: bold;">Close the developer console and click the Reload button below</p>
                <button onclick="window.location.href='/'" style="
                  background: #0055A6;
                  color: #fff;
                  border: none;
                  padding: 12px 32px;
                  font-size: 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  margin-top: 20px;
                  font-family: inherit;
                ">Reload</button>
              </div>
            \`;
          }
        }, 1000);
      </script>
    </body>
    </html>
  `);
  document.close();
};

// Block keyboard shortcuts
const blockKeyboardShortcuts = (e: KeyboardEvent): void => {
  const mac = isMac();

  // F12 - All platforms
  if (e.key === "F12" || e.keyCode === 123) {
    e.preventDefault();
    e.stopPropagation();
    handleDevToolsDetected();
    return;
  }

  // Ctrl+Shift+I / Cmd+Option+I (Inspector)
  if (
    (mac && e.metaKey && e.altKey && e.key.toLowerCase() === "i") ||
    (!mac && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "i")
  ) {
    e.preventDefault();
    e.stopPropagation();
    handleDevToolsDetected();
    return;
  }

  // Ctrl+Shift+J / Cmd+Option+J (Console)
  if (
    (mac && e.metaKey && e.altKey && e.key.toLowerCase() === "j") ||
    (!mac && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "j")
  ) {
    e.preventDefault();
    e.stopPropagation();
    handleDevToolsDetected();
    return;
  }

  // Ctrl+Shift+C / Cmd+Option+C / Cmd+Shift+C (Element selector)
  if (
    (mac &&
      ((e.metaKey && e.altKey) || (e.metaKey && e.shiftKey)) &&
      e.key.toLowerCase() === "c") ||
    (!mac && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "c")
  ) {
    e.preventDefault();
    e.stopPropagation();
    handleDevToolsDetected();
    return;
  }

  // Ctrl+U / Cmd+U (View source)
  if (
    (mac && e.metaKey && e.key.toLowerCase() === "u") ||
    (!mac && e.ctrlKey && e.key.toLowerCase() === "u")
  ) {
    e.preventDefault();
    e.stopPropagation();
    handleDevToolsDetected();
    return;
  }

  // Ctrl+Shift+K (Firefox Console)
  if (!mac && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "k") {
    e.preventDefault();
    e.stopPropagation();
    handleDevToolsDetected();
    return;
  }
};

// Block right-click context menu
const blockContextMenu = (e: MouseEvent): void => {
  e.preventDefault();
  e.stopPropagation();
  return;
};

// Detect DevTools via window size difference
let devToolsCheckInterval: ReturnType<typeof setInterval> | null = null;

const checkDevToolsBySize = (): void => {
  const widthThreshold = window.outerWidth - window.innerWidth > 160;
  const heightThreshold = window.outerHeight - window.innerHeight > 160;

  if (widthThreshold || heightThreshold) {
    handleDevToolsDetected();
  }
};

// Detect DevTools via console.log timing/behavior
const checkDevToolsByConsole = (): void => {
  const element = new Image();
  Object.defineProperty(element, "id", {
    get: function () {
      handleDevToolsDetected();
      return "";
    },
  });

  // This will trigger the getter if devtools is open and inspecting
  console.log("%c", element);
  console.clear();
};

// Initialize all blockers
export const initDevToolsBlocker = (): void => {
  if (!ENABLE_DEVTOOLS_BLOCKER) {
    console.log("DevTools blocker is disabled");
    return;
  }

  // Setup BroadcastChannel to sync across tabs
  if (typeof BroadcastChannel !== "undefined") {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastChannel.onmessage = (event) => {
      if (event.data?.type === "DEVTOOLS_DETECTED") {
        // Another tab detected devtools, trigger warning here too
        handleDevToolsDetected(false); // false = don't notify other tabs again
      }
    };
  }

  // Add keyboard listener
  document.addEventListener("keydown", blockKeyboardShortcuts, true);

  // Block right-click
  document.addEventListener("contextmenu", blockContextMenu, true);

  // Start periodic checks for DevTools
  devToolsCheckInterval = setInterval(() => {
    if (!isBlocked) {
      checkDevToolsBySize();
      checkDevToolsByConsole();
    }
  }, 1000);

  // Initial check
  checkDevToolsBySize();

  console.clear();
};

// Cleanup function (for testing purposes)
export const destroyDevToolsBlocker = (): void => {
  document.removeEventListener("keydown", blockKeyboardShortcuts, true);
  document.removeEventListener("contextmenu", blockContextMenu, true);

  if (devToolsCheckInterval) {
    clearInterval(devToolsCheckInterval);
  }

  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }

  isBlocked = false;
};
